import { useCallback, useState } from "react";

import { toast } from "react-toastify";

import { useDispatch } from "react-redux";

import {
  updateUserPlan,
  updateUserCredits,
} from "../../../actions/userActions";

import { selectPlanApi } from "../../../services/api/pitbPayzenApi";

import { getCardPaymentFormToken } from "../../../services/api/getPaymentCardFormToken";

import { activateFreePlan } from "../../../services/api/getAllPlans";

import { getActivatedPlanId, getConsumerNumber } from "../../paymentUtils";

import { isFreePlan } from "../../planUtils";

import { renewCreditsApi } from "../../../services/api/creditApi";

export default function useSubscriptionPayment({
  userId,
  selectedPlan,
  setSelectedPlan,
  savePsid,
  clearPsid,
}) {
  const dispatch = useDispatch();

  const [dialogs, setDialogs] = useState({
    freePlan: false,
    paymentMethod: false,
    psidDetails: false,
    card: false,
  });

  const [loading, setLoading] = useState(false);

  const [formToken, setFormToken] = useState("");

  const openPlan = useCallback((plan) => {
    if (!plan) {
      return;
    }

    if (isFreePlan(plan)) {
      setDialogs({
        freePlan: true,
        paymentMethod: false,
        psidDetails: false,
        card: false,
      });

      return;
    }

    setDialogs({
      freePlan: false,
      paymentMethod: true,
      psidDetails: false,
      card: false,
    });
  }, []);

  const closeFreePlanDialog = useCallback(() => {
    if (loading) {
      return;
    }

    setDialogs((prev) => ({
      ...prev,
      freePlan: false,
    }));
  }, [loading]);

  const closePaymentMethodDialog = useCallback(() => {
    if (loading) {
      return;
    }

    setDialogs((prev) => ({
      ...prev,
      paymentMethod: false,
    }));
  }, [loading]);

  const closePsidDialog = useCallback(() => {
    setDialogs((prev) => ({
      ...prev,
      psidDetails: false,
    }));
  }, []);

  const closeCardModal = useCallback(() => {
    setDialogs((prev) => ({
      ...prev,
      card: false,
    }));
  }, []);

  const openPsidDialog = useCallback(() => {
    setDialogs((prev) => ({
      ...prev,
      psidDetails: true,
    }));
  }, []);

  const confirmFreePlan = useCallback(async () => {
    if (!userId || !selectedPlan) {
      toast.error("User authentication required. Please log in again.");

      return;
    }

    setLoading(true);

    try {
      const response = await activateFreePlan({
        userId,
        selectedPlan: selectedPlan.title,
      });

      if (response.data?.status === "OK" || response.status === 200) {
        const activePlanId =
          response.data?.data?.plan_id || selectedPlan.id || selectedPlan.title;

        dispatch(updateUserPlan(activePlanId));

        toast.success("Free plan activated successfully!");

        setDialogs((prev) => ({
          ...prev,
          freePlan: false,
        }));
      } else {
        toast.error(response.data?.message || "Failed to activate Free plan.");
      }
    } catch (error) {
      console.error("Error activating free plan:", error);

      toast.error(
        error.response?.data?.message || "Failed to activate Free plan.",
      );
    } finally {
      setLoading(false);
    }
  }, [userId, selectedPlan, dispatch]);

  const confirmPayment = useCallback(
    async (plan, method) => {
      if (!userId) {
        toast.error("User authentication required. Please log in again.");

        return;
      }

      setLoading(true);

      if (method === "card") {
        try {
          const response = await getCardPaymentFormToken(userId, plan.title);

          setFormToken(response.data.formToken);

          setDialogs({
            freePlan: false,
            paymentMethod: false,
            psidDetails: false,
            card: true,
          });
        } catch (error) {
          console.error("Failed to fetch PayZen form token:", error);

          toast.error(
            error.response?.data?.message ||
              "Could not initialize card payment. Please try again.",
          );
        } finally {
          setLoading(false);
        }

        return;
      }

      try {
        const response = await selectPlanApi(userId, plan.title);

        const consumerNo = getConsumerNumber(response);

        if (
          response.data?.status === "OK" ||
          response.data?.consumerNumber ||
          response.data?.psid
        ) {
          savePsid(response.data);

          setDialogs({
            freePlan: false,
            paymentMethod: false,
            psidDetails: true,
            card: false,
          });
        } else {
          toast.error(
            response.data?.message || "Failed to generate PSID voucher.",
          );
        }
      } catch (error) {
        console.error("Error calling selectPlan API:", error);

        const serverMessage = error.response?.data?.message || "";

        toast.error(
          serverMessage || "An error occurred while processing your request.",
        );
      } finally {
        setLoading(false);
      }
    },
    [userId, savePsid],
  );

  const handlePsidSuccess = useCallback(
    (activatedPlanId) => {
      setDialogs((prev) => ({
        ...prev,
        psidDetails: false,
      }));

      if (activatedPlanId) {
        dispatch(updateUserPlan(activatedPlanId));
      }

      clearPsid();
      setSelectedPlan(null);
    },
    [dispatch, clearPsid, setSelectedPlan],
  );

  const handleCardSuccess = useCallback(
    async (response) => {
      setDialogs((prev) => ({
        ...prev,
        card: false,
      }));

      const updatedPlanId =
        response?.data?.planId || selectedPlan?.id || selectedPlan?.title;

      dispatch(updateUserPlan(updatedPlanId));

      setSelectedPlan(null);
      setFormToken("");

      toast.success(
        "Payment successful! Your subscription has been activated.",
      );

      try {
        const res = await renewCreditsApi(userId, selectedPlan?.title);
        console.log("user credits from api", res.data.user.credits);
        dispatch(updateUserCredits(res?.data?.user?.credits));
        toast.success("Subscription credits renewed successfully.");
      } catch (error) {
        toast.error("Failed to renew subscription credits");
      }
    },
    [dispatch, selectedPlan, setSelectedPlan],
  );

  const handleCardError = useCallback((errorDetails) => {
    console.error("Card Payment Error:", errorDetails);
  }, []);

  return {
    dialogs,
    loading,
    formToken,

    openPlan,

    closeFreePlanDialog,
    closePaymentMethodDialog,
    closePsidDialog,
    closeCardModal,

    openPsidDialog,

    confirmFreePlan,
    confirmPayment,

    handlePsidSuccess,
    handleCardSuccess,
    handleCardError,
  };
}
